import type { FormEvent } from 'react'
import type { Sequence, SequenceStep } from '@/constants/crm'

import { useState } from 'react'
import Button from '@/components/ui/button'
import Field from '@/components/ui/field'
import FormSection from '@/components/companies/new-company/form-section'
import StepsEditor, { cleanSteps } from '@/components/sequences/steps-editor'
import { Input } from '@/components/ui/input'
import { OptionSelect, OwnerSelect } from '@/components/generals/form-selects'
import { OWNERS } from '@/constants/companies'
import { apiRequest } from '@/lib/client-api'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

const START_OPTIONS = ['Draft', 'Active']

const DEFAULT_STEPS: SequenceStep[] = [
    { day: 0, channel: 'Email', subject: '' },
    { day: 3, channel: 'Call', subject: '' }
]

function SequenceForm() {
    const upsertSequence = useCrmStore(state => state.upsertSequence)
    const closeDialog = useCrmStore(state => state.closeDialog)
    const openSequence = useCrmStore(state => state.openSequence)
    const showNotice = useCrmStore(state => state.showNotice)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [owner, setOwner] = useState(OWNERS[0].name)
    const [status, setStatus] = useState('Draft')
    const [steps, setSteps] = useState<SequenceStep[]>(DEFAULT_STEPS)
    const [errors, setErrors] = useState<{ name?: string, steps?: string }>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()

        if (submitting) return

        const cleaned = cleanSteps(steps)
        const nextErrors = {
            name: name.trim() ? undefined : "Give the sequence a name.",
            steps: cleaned.length > 0 ? undefined : "Add at least one step with a subject."
        }

        setErrors(nextErrors)

        if (nextErrors.name || nextErrors.steps) return

        setSubmitting(true)
        setFormError(null)

        const result = await apiRequest<{ sequence: Sequence }>('/api/sequences', 'POST', {
            name: name.trim(),
            description: description.trim(),
            owner,
            status,
            steps: cleaned
        })

        setSubmitting(false)

        if (!result.ok) {
            setFormError(result.message)

            return
        }

        upsertSequence(result.data.sequence)
        closeDialog()
        openSequence(result.data.sequence.id)
        showNotice('success', "Sequence created.")
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='flex flex-col' aria-busy={submitting}>
            <DialogHeader>
                <DialogTitle>New Sequence</DialogTitle>
                <DialogDescription>
                    Plan a series of outreach steps your team works through.
                </DialogDescription>
            </DialogHeader>

            <FormSection title="Sequence">
                <Field label="Name" htmlFor='sequence-name' required error={errors.name}>
                    <Input
                        id='sequence-name'
                        value={name}
                        onChange={event => {
                            setName(event.target.value)
                            if (errors.name) setErrors(current => ({ ...current, name: undefined }))
                        }}
                        placeholder="Webinar follow-up"
                        maxLength={120}
                        autoComplete='off'
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'sequence-name-error' : undefined}
                        className='aria-invalid:border-danger'
                        autoFocus
                    />
                </Field>
                <Field label="Description" htmlFor='sequence-description'>
                    <Input
                        id='sequence-description'
                        value={description}
                        onChange={event => setDescription(event.target.value)}
                        placeholder="Who this is for and what it should achieve"
                        maxLength={240}
                        autoComplete='off'
                    />
                </Field>
                <div className='grid gap-4 sm:grid-cols-2'>
                    <Field label="Owner" htmlFor='sequence-owner'>
                        <OwnerSelect id='sequence-owner' value={owner} onChange={setOwner} />
                    </Field>
                    <Field label="Start as" htmlFor='sequence-status'>
                        <OptionSelect id='sequence-status' value={status} onChange={setStatus} options={START_OPTIONS} />
                    </Field>
                </div>
            </FormSection>

            <FormSection title="Steps">
                <StepsEditor idPrefix='new-sequence' steps={steps} onChange={setSteps} disabled={submitting} />
                {errors.steps && (
                    <span role='alert' className='caption-style text-danger block'>
                        {errors.steps}
                    </span>
                )}
            </FormSection>

            {formError && (
                <div role='alert' className='caption-style text-danger px-6 pb-4'>
                    {formError}
                </div>
            )}

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant='subtle' size='sm'>
                        Cancel
                    </Button>
                </DialogClose>
                <Button variant='primary' size='sm' type='submit' disabled={submitting}>
                    <PlusIcon aria-hidden className='size-3' />
                    {submitting ? "Saving…" : "Create Sequence"}
                </Button>
            </DialogFooter>
        </form>
    )
}

export default function SequenceDialog() {
    const open = useCrmStore(state => state.dialog === 'sequence')
    const closeDialog = useCrmStore(state => state.closeDialog)

    return (
        <Dialog open={open} onOpenChange={next => !next && closeDialog()}>
            <DialogContent className='max-w-[640px]'>
                <SequenceForm />
            </DialogContent>
        </Dialog>
    )
}
